/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge } from "@/subframe/components/Badge";
import FriendInfo from "./FriendInfo";
import FriendInfoModal from "./FriendInfoModal";
import Templates from "./Templates";
import Message from "./Message";
import { Recipient } from "../redux/interfaces/reducersInterfaces";
import { MessagesDataWithPagination } from "../Interfaces/messagesInterface";
import TextMessageSend from "./TextMessageSend";
import { useEffect, useState } from "react";
import { IconButton } from "@/subframe/components/IconButton";

interface ChatRoomProps {
  currentRecipient: Recipient | null;
  messages: MessagesDataWithPagination;
  scrollRef: React.RefObject<HTMLDivElement>;
  // setSelectDateAndTimePopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSwitchOn: boolean;
  onToggle: () => void;
  handleAiToHuman: () => void;
}

function ChatRoom({
  currentRecipient,
  messages,
  scrollRef,
  // setSelectDateAndTimePopupOpen,
  isSwitchOn,
  onToggle,
  handleAiToHuman,
}: ChatRoomProps) {
  const [is24HoursWindowShow, setIs24HoursWindowShow] = useState<boolean>(true);
  const [showFriendInfo, setShowFriendInfo] = useState<boolean>(() => {
    const savedState = localStorage.getItem('friendInfoVisible');
    return savedState !== null ? JSON.parse(savedState) : false;
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);

  // Check screen width on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 1100);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    if (!currentRecipient?.recipient_last_message_time) {
      setIs24HoursWindowShow(true);
      return;
    }

    const lastMessagePassed =
      currentRecipient.recipient_last_message_time + 24 * 60 * 60 * 1000 <=
      Date.now();

    setIs24HoursWindowShow(lastMessagePassed);
  }, [currentRecipient?.recipient_last_message_time]);

  const toggleFriendInfo = () => {
    const newState = !showFriendInfo;
    setShowFriendInfo(newState);
    localStorage.setItem('friendInfoVisible', JSON.stringify(newState));
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="flex grow shrink-0 basis-0 flex-col items-start self-stretch bg-gray-50">
        <div className="flex w-full items-center justify-between border-b border-solid border-gray-300 bg-white px-6 py-4">
          <Badge className="hidden" variant="warning">
            Needs Human
          </Badge>
          <span className="grow shrink-0 basis-0 text-body-bold font-body-bold text-default-font text-center">
            {currentRecipient && currentRecipient.name
              ? currentRecipient.name
              : "No name available"}
          </span>
          {/* Show different icons based on screen size */}
          {isSmallScreen ? (
            <IconButton
              size="small"
              icon="FeatherInfo"
              onClick={openModal}
              title="Show Contact Info"
              className="transition-transform duration-300 ease-in-out hover:scale-110"
            />
          ) : (
            <IconButton
              size="small"
              icon={showFriendInfo ? "FeatherChevronRight" : "FeatherChevronLeft"}
              onClick={toggleFriendInfo}
              title={showFriendInfo ? "Hide Info" : "Show Info"}
              className="transition-transform duration-300 ease-in-out hover:scale-110"
            />
          )}
        </div>
        {/* Messages */}
        <Message
          messages={messages as any}
          scrollRef={scrollRef}
          is24HoursWindowShow={is24HoursWindowShow}
        />
        {/* <MediaMessageSend/> */}
        {/* Templates */}
        <Templates currentRecipient={currentRecipient} />

        {/* Message send */}
        {!is24HoursWindowShow && (
          <TextMessageSend
            currentRecipient={currentRecipient}
            // setSelectDateAndTimePopupOpen={setSelectDateAndTimePopupOpen}
            handleAiToHuman={handleAiToHuman}
          />
        )}
      </div>

      {/* Friend Info component - only show on larger screens */}
      {!isSmallScreen && (
        <div 
          className={`transition-all duration-300 ease-in-out ${
            showFriendInfo ? 'w-80 opacity-100' : 'w-0 opacity-0'
          }`}
        >
          {showFriendInfo && (
            <FriendInfo
              currentRecipient={currentRecipient}
              messages={messages}
              isSwitchOn={isSwitchOn}
              onToggle={onToggle}
            />
          )}
        </div>
      )}

      {/* Friend Info Modal - for smaller screens */}
      <FriendInfoModal
        currentRecipient={currentRecipient}
        messages={messages}
        isSwitchOn={isSwitchOn}
        onToggle={onToggle}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </>
  );
}

export default ChatRoom;
